import { GetServerSideProps } from 'next';
import { UrlShortenerService } from '@/lib/services/urlShortenerService';
import axios from 'axios';
import Link from 'next/link';

const CUSTOMER_IO_WEBHOOK_URL = process.env.CUSTOMER_IO_EVENT_REDIRECT_URL || 'https://api.customer.io/v1/webhook/73725d37afb84cdb';

// Página de redirecionamento ou erro
const ShortenerPage = ({ expired = false }) => {
  // Se o link está expirado ou não foi encontrado, mostrar página de erro
  if (expired) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[#fefefe]">
        <div className="p-8 max-w-md bg-white rounded-lg shadow-md border border-gray-100 text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#ff0080] flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-12 h-12 text-white">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-[#ff0080] mb-4">Link expirado</h1>
          <p className="mt-2 text-gray-700 mb-6">
            Este link não está mais disponível. Precisa de ajuda? Fale com o time da Darwin.
          </p>
          <Link 
            href="https://drwn.in/whatsapp" 
            className="inline-block px-6 py-3 bg-[#ff0080] text-white font-medium rounded-lg hover:bg-[#e00070] transition-colors"
          >
            Falar com a Darwin
          </Link>
          <div className="mt-6 pt-6 border-t border-gray-100 text-sm text-gray-500">
            <p>© {new Date().getFullYear()} Darwin Seguros</p>
          </div>
        </div>
      </div>
    );
  }

  // Página de redirecionamento padrão
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
      <div className="p-6 max-w-sm bg-white rounded-lg shadow-md">
        <h1 className="text-2xl font-semibold text-[#ff0080]">Redirecionando...</h1>
        <p className="mt-2 text-gray-600">Você será redirecionado automaticamente.</p>
        <div className="mt-4 w-full bg-gray-200 h-1.5 rounded-full overflow-hidden">
          <div className="bg-[#ff0080] h-full rounded-full animate-pulse"></div>
        </div>
      </div>
    </div>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ params, req, res }) => {
  const shortCode = params?.shortCode as string;

  try {
    // Obter a URL original
    const urlData = await UrlShortenerService.getOriginalUrl(shortCode);
    
    if (!urlData) {
      // Se não encontrou a URL, mostrar página de erro
      return {
        props: {
          expired: true
        }
      };
    }
    
    const { originalUrl, cioId, eventName, accessCount, proposalId, forBroker } = urlData;
    
    // Log de redirecionamento
    console.log(`Redirecionando [${shortCode}] para ${originalUrl} (Acesso #${accessCount}) - ${forBroker ? 'CORRETOR' : 'CLIENTE'}`);
    console.log(`Dados de rastreamento: cioId=${cioId}, eventName=${eventName}, proposalId=${proposalId}, forBroker=${forBroker}`);
    
    // Se tiver cioId, enviar evento para o Customer.io e RudderStack
    if (cioId) {
      try {
        const webhookData = {
          event_name: eventName || 'whatsapp_button_click',
          redirect_url: originalUrl,
          cio_id: cioId,
          short_code: shortCode,
          access_count: accessCount,
          proposal_id: proposalId || null,
          timestamp: Math.floor(Date.now() / 1000),
          user_agent: req.headers['user-agent'],
          ip_address: req.headers['x-forwarded-for'] || req.socket.remoteAddress
        };
        
        console.log('Enviando eventos:', JSON.stringify(webhookData, null, 2));
        
        // Enviar evento para o Customer.io
        await axios.post(CUSTOMER_IO_WEBHOOK_URL, webhookData);

        // Enviar evento para o RudderStack
        const rudderStackWebhook = process.env.RUDDERSTACK_WEBHOOK_URL;
        if (rudderStackWebhook) {
          const rudderStackPayload = {
            type: "track",
            event: "link_click",
            anonymousId: cioId || shortCode || Date.now().toString(),
            properties: {
              event_name: eventName || 'whatsapp_button_click',
              redirect_url: originalUrl,
              cio_id: cioId,
              short_code: shortCode,
              access_count: accessCount,
              proposal_id: proposalId || null,
              timestamp: Math.floor(Date.now() / 1000),
              user_agent: req.headers['user-agent'],
              ip_address: req.headers['x-forwarded-for'] || req.socket.remoteAddress
            }
          };

          // Enviar evento de forma assíncrona para não atrasar o redirecionamento
          axios.post(rudderStackWebhook, rudderStackPayload)
            .catch(error => console.error('Erro ao enviar evento para RudderStack:', error));
        }
      } catch (error) {
        console.error('Erro ao enviar eventos:', error);
        // Não impedir o redirecionamento se houver falha no envio dos eventos
      }
    }
    
    // Redirecionar para a URL original
    return {
      redirect: {
        destination: originalUrl,
        permanent: false,
      },
    };
  } catch (error) {
    console.error('Erro ao processar redirecionamento:', error);
    
    // Em caso de erro, mostrar página de erro
    return {
      props: {
        expired: true
      }
    };
  }
};

export default ShortenerPage; 